import React from "react";

/**
 * Top-level React error boundary. Catches any render error and shows a
 * branded, friendly fallback instead of a blank screen. In production we
 * also try to ping `/api/log/client-error` (best-effort).
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("Cowrieking caught a render error:", error, info);
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/log/client-error`;
      const body = JSON.stringify({
        message: String(error?.message || error),
        stack: String(info?.componentStack || ""),
        url: window.location.href,
        ua: navigator.userAgent,
      });
      navigator.sendBeacon?.(url, body);
    } catch (_) { /* best-effort */ }
  }

  reset = () => {
    this.setState({ hasError: false, error: null });
    window.location.href = "/";
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <main
        data-testid="error-boundary-fallback"
        className="min-h-screen flex items-center justify-center bg-[#0a0908] px-6"
      >
        <div className="max-w-lg text-center">
          <p className="text-[11px] font-mono uppercase tracking-[0.35em] text-[#d4af37] mb-5">
            Something broke gracefully
          </p>
          <h1 className="font-serif text-3xl sm:text-4xl text-[#f4f0ec] font-light leading-tight mb-5">
            We caught the fall.
          </h1>
          <p className="text-[#a8a29e] leading-relaxed mb-8 font-sans">
            A piece of the page misbehaved. The rest of the site is still standing — return home and try again.
          </p>
          <button
            onClick={this.reset}
            data-testid="error-boundary-reset"
            className="btn-primary"
          >
            Return Home
          </button>
        </div>
      </main>
    );
  }
}
